import { PALETTE, PIP, type Hex } from "@eldrun/content";
import { accentTokensFor } from "@eldrun/web-ui";

/**
 * Partes puras do kit da UI (F79): valores de controle, texto dos pips, navegação das abas e o tema. O DOM
 * (`components.ts`) fica fino em volta disto, e os testes rodam em node, sem jsdom.
 */

/** Os nomes dos ícones do kit; o desenho de cada um (SVG do Lucide) está em `kit-icons.ts` (F113). */
export const KIT_ICONS = [
  "icone-jogar",
  "icone-continuar",
  "icone-multijogador",
  "icone-opcoes",
  "icone-sair",
  "icone-conta",
  "icone-novidades",
  "icone-chevron",
  "icone-site",
  "icone-inicio",
  "icone-salvar",
  "icone-tela",
  "icone-som",
  "icone-controle",
  "icone-voltar",
  "icone-restaurar",
  "icone-confirmar",
  "icone-triangulo",
  "icone-x",
  "hud-opcoes",
  "hud-mochila",
  "hud-mapa",
  "hud-pausa",
  // Botões do canto na coluna da folha de elementos (F109).
  "hud-icone-opcoes",
  "hud-icone-mochila",
  "hud-icone-mapa",
  "hud-icone-menu",
  "hud-interagir",
  "hud-alerta",
  "icone-inventario",
  "icone-fabricacao",
  "vital-vida",
  "vital-fome",
  "vital-folego",
  "aba-bencaos",
  "aba-habilidades",
  "categoria-todos",
  "categoria-ferramentas",
  "categoria-armas",
  "categoria-armaduras",
  "categoria-consumiveis",
  "categoria-materiais",
  "categoria-construcao",
  // Painéis da F82 (ui-g, ui-e).
  "icone-mapa",
  "mapa-bussola",
  "mapa-mais",
  "mapa-menos",
  "icone-centralizar",
  "icone-legenda",
  "icone-detalhe",
  "icone-ficha",
  "icone-perfil",
  "icone-atributos",
  "icone-talentos",
  "icone-biografia",
  "icone-nivel",
  "icone-classe",
  "icone-defesa",
  "icone-velocidade",
  "icone-mira",
  // Inventário e fabricação (F82): ações e atributos do detalhe (ui-c), a seta do 2 × 2 (ui-f).
  "icone-mover",
  "icone-dividir",
  "icone-usar",
  "icone-largar",
  "icone-pilha",
  "icone-moedas",
  "icone-itens",
  "icone-seta",
  // Menu (F113): um desenho por gesto (antes o "continuar" e o "multijogador" serviam a dois).
  "icone-codigo",
  "icone-feedback",
  "icone-rankings",
] as const;
export type KitIcon = (typeof KIT_ICONS)[number];

/** Pip vazio (◇), par do `PIP` (◆) do conteúdo. */
export const PIP_EMPTY = "◇";

/** Pips cheios e vazios de `n` em `max` (◆◆◇◇): `n` limitado a `[0, max]` e inteiro. */
export function pipsText(n: number, max: number): { full: string; empty: string } {
  const total = Math.max(0, Math.floor(max));
  const full = Math.max(0, Math.min(total, Math.floor(n)));
  return { full: PIP.repeat(full), empty: PIP_EMPTY.repeat(total - full) };
}

/** Valor de controle deslizante: dentro de `[min, max]` e no passo mais próximo a partir de `min`. */
export function clampStep(value: number, min: number, max: number, step: number): number {
  if (!Number.isFinite(value)) return min;
  const clamped = Math.min(max, Math.max(min, value));
  if (!(step > 0)) return clamped;
  const snapped = min + Math.round((clamped - min) / step) * step;
  // Corrige o erro de ponto flutuante do passo (0.1 × 3) pelo número de casas do passo.
  const decimals = (String(step).split(".")[1] ?? "").length;
  return Math.min(max, Number(snapped.toFixed(decimals)));
}

/** Fração de `value` em `[min, max]` (0 a 1), para o preenchimento da trilha e das barras. */
export function fraction(value: number, min: number, max: number): number {
  if (!(max > min) || !Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, (value - min) / (max - min)));
}

/** Índice do seletor ‹ valor ›: anda `delta` em `count` opções; dá a volta se `wrap`, senão para na ponta. */
export function stepIndex(index: number, delta: number, count: number, wrap = false): number {
  if (count <= 0) return 0;
  const next = Math.floor(index) + Math.trunc(delta);
  if (wrap) return ((next % count) + count) % count;
  return Math.min(count - 1, Math.max(0, next));
}

/** Navegação das abas pelo teclado (padrão ARIA de `tablist`): setas dão a volta, Home e End vão às pontas. */
export function tabKeyTarget(key: string, index: number, count: number): number | undefined {
  if (count <= 0) return undefined;
  if (key === "ArrowRight" || key === "ArrowDown") return stepIndex(index, 1, count, true);
  if (key === "ArrowLeft" || key === "ArrowUp") return stepIndex(index, -1, count, true);
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return undefined;
}

/** Texto de uma barra: `valor / máximo` arredondados (Vida 82 / 100). */
export function barText(value: number, max: number): string {
  return `${Math.round(Math.max(0, value))} / ${Math.round(Math.max(0, max))}`;
}

type Themed = { dataset: Record<string, string | undefined>; style: { setProperty(name: string, value: string): void; removeProperty(name: string): string } };

/**
 * Tema da UI (F79; F85 pela classe; F113 no sistema visual, ADR 0024): `data-tema="neutro"` (quem ainda
 * não escolheu classe: o acento é o neutro) ou `data-tema="classe"` com o acento na cor da classe (a
 * variante daltônica quando o modo está ligado). Grava os tokens do `@eldrun/web-ui` calculados para a
 * cor (`--accent`, a tinta `--accent-foreground`, o texto legível `--accent-text` e o foco `--ring`, os
 * do modo escuro) e os nomes antigos do jogo como apelidos (`--acento`, `--acento-claro`). Vale para o
 * `body` ou para um bloco (a vitrine mostra os dois).
 */
export function applyTheme(element: Themed, accent?: string): void {
  const color = accent ?? PALETTE.neutro.hex;
  const t = accentTokensFor(color as Hex);
  element.dataset["tema"] = accent ? "classe" : "neutro";
  const props: Record<string, string> = { "--accent": color, "--accent-foreground": t.foreground, "--accent-text": t.text.dark, "--ring": t.ring.dark, "--acento": color, "--acento-claro": t.text.dark };
  for (const [name, value] of Object.entries(props)) element.style.setProperty(name, value);
}
